import { useState, useCallback, useEffect, useRef } from 'react';
import { calculateDistance } from '../utils/distance';

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 500;

export interface BusinessPlace {
  placeId: string;
  name: string;
  address: string;
  latitude?: number;
  longitude?: number;
  distance?: string;
}

export interface BusinessPlaceDetails {
  placeId: string;
  name: string;
  formattedAddress: string;
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  postcode: string;
  latitude?: number;
  longitude?: number;
  phoneNumber?: string;
  website?: string;
}

interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

interface UseBusinessSearchProps {
  location?: LocationCoordinates | null;
}

interface UseBusinessSearchReturn {
  searchQuery: string;
  businesses: BusinessPlace[];
  isLoading: boolean;
  hasSearched: boolean;
  handleSearchChange: (text: string) => void;
  handleBusinessSelect: (business: BusinessPlace) => Promise<BusinessPlaceDetails | null>;
}

// Declare google maps types
declare global {
  interface Window {
    google: typeof google;
  }
}

// Wait for Google Maps to load
const waitForGoogleMaps = (): Promise<void> => {
  return new Promise((resolve) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }

    const checkInterval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        clearInterval(checkInterval);
        resolve();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      resolve();
    }, 10000);
  });
};

export const useBusinessSearch = (props?: UseBusinessSearchProps): UseBusinessSearchReturn => {
  const location = props?.location;
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [businesses, setBusinesses] = useState<BusinessPlace[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [isGoogleReady, setIsGoogleReady] = useState<boolean>(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);

  // Initialize Google Maps Places Service
  useEffect(() => {
    const initGoogleMaps = async () => {
      await waitForGoogleMaps();

      if (window.google && window.google.maps && window.google.maps.places) {
        // Create a dummy div for PlacesService (required by the API)
        const dummyDiv = document.createElement('div');
        placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
        setIsGoogleReady(true);
      }
    };

    initGoogleMaps();
  }, []);

  // Search using Google Places Text Search
  const searchBusinesses = useCallback(async (query: string): Promise<void> => {
    if (!query.trim()) {
      setBusinesses([]);
      setHasSearched(false);
      return;
    }

    if (!isGoogleReady || !placesServiceRef.current) {
      console.warn('Google Places API is not ready yet');
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      const request: google.maps.places.TextSearchRequest = {
        query: query,
        type: 'establishment',
      };

      // Add location bias if available
      if (location) {
        request.location = new window.google.maps.LatLng(location.latitude, location.longitude);
        request.radius = 50000; // 50km radius
      }

      placesServiceRef.current.textSearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          const mappedResults: BusinessPlace[] = results.map((place) => {
            const placeLatitude = place.geometry?.location?.lat();
            const placeLongitude = place.geometry?.location?.lng();

            // Calculate distance if we have both locations
            const distance = calculateDistance(
              location,
              placeLatitude !== undefined && placeLongitude !== undefined
                ? { latitude: placeLatitude, longitude: placeLongitude }
                : null
            );

            return {
              placeId: place.place_id || '',
              name: place.name || '',
              address: place.formatted_address || '',
              latitude: placeLatitude,
              longitude: placeLongitude,
              distance,
            };
          });
          setBusinesses(mappedResults);
        } else {
          setBusinesses([]);
        }
        setIsLoading(false);
      });
    } catch (error) {
      console.error('Error searching businesses:', error);
      setBusinesses([]);
      setIsLoading(false);
    }
  }, [location, isGoogleReady]);

  // Debounced search change handler
  const handleSearchChange = useCallback(
    (text: string): void => {
      setSearchQuery(text);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        searchBusinesses(text);
      }, DEBOUNCE_DELAY);
    },
    [searchBusinesses]
  );

  // Fetch detailed place information using Google Places Details
  const fetchPlaceDetails = useCallback(
    async (placeId: string): Promise<BusinessPlaceDetails | null> => {
      if (!isGoogleReady || !placesServiceRef.current) {
        console.warn('Google Places API is not ready yet');
        return null;
      }

      return new Promise((resolve) => {
        const request: google.maps.places.PlaceDetailsRequest = {
          placeId: placeId,
          fields: [
            'name',
            'formatted_address',
            'address_components',
            'geometry',
            'formatted_phone_number',
            'website',
          ],
        };

        placesServiceRef.current!.getDetails(request, (result, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && result) {
            const addressComponents = result.address_components || [];

            const getAddressComponent = (types: string[]): string => {
              const component = addressComponents.find((comp) =>
                types.some((type) => comp.types.includes(type))
              );
              return component?.long_name || '';
            };

            const streetNumber = getAddressComponent(['street_number']);
            const route = getAddressComponent(['route']);
            const streetAddress = [streetNumber, route].filter(Boolean).join(' ');

            const placeDetails: BusinessPlaceDetails = {
              placeId,
              name: result.name || '',
              formattedAddress: result.formatted_address || '',
              streetAddress:
                streetAddress ||
                getAddressComponent(['sublocality_level_1', 'sublocality']),
              city: getAddressComponent(['locality', 'administrative_area_level_2']),
              state: getAddressComponent(['administrative_area_level_1']),
              country: getAddressComponent(['country']),
              postcode: getAddressComponent(['postal_code']),
              latitude: result.geometry?.location?.lat(),
              longitude: result.geometry?.location?.lng(),
              phoneNumber: result.formatted_phone_number,
              website: result.website,
            };

            resolve(placeDetails);
          } else {
            resolve(null);
          }
        });
      });
    },
    [isGoogleReady]
  );

  // Handle business selection - fetch details
  const handleBusinessSelect = useCallback(
    async (business: BusinessPlace): Promise<BusinessPlaceDetails | null> => {
      setIsLoading(true);
      const placeDetails = await fetchPlaceDetails(business.placeId);
      setIsLoading(false);
      return placeDetails;
    },
    [fetchPlaceDetails]
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    searchQuery,
    businesses,
    isLoading,
    hasSearched,
    handleSearchChange,
    handleBusinessSelect,
  };
};

export default useBusinessSearch;
